import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import type { BreadcrumbItem } from './breadcrumb';

interface CrumbEntry {
  id: symbol;
  level: number;
  crumb: BreadcrumbItem;
}

type BreadcrumbContextValue = {
  crumbs: BreadcrumbItem[];
  addCrumb: (id: symbol, level: number, item: BreadcrumbItem) => void;
  removeCrumb: (id: symbol) => void;
};

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);
const BreadcrumbLevelContext = createContext(0);

const BreadcrumbProvider = ({ children }: { readonly children: ReactNode }) => {
  const [entries, setEntries] = useState<CrumbEntry[]>([]);

  const addCrumb = useCallback((id: symbol, level: number, crumb: BreadcrumbItem) => {
    setEntries((prev) => {
      const entry = { id, level, crumb };
      const idx = prev.findIndex((e) => e.level > level);
      return idx === -1 ? [...prev, entry] : [...prev.slice(0, idx), entry, ...prev.slice(idx)];
    });
  }, []);

  const removeCrumb = useCallback((id: symbol) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const value = useMemo<BreadcrumbContextValue>(
    () => ({ crumbs: entries.map((e) => e.crumb), addCrumb, removeCrumb }),
    [entries, addCrumb, removeCrumb]
  );

  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
};

const useBreadcrumb = () => {
  const ctx = useContext(BreadcrumbContext);
  if (!ctx) throw new Error('useBreadcrumb must be used within BreadcrumbProvider');
  return ctx;
};

const useDynamicCrumb = (label: string | undefined) => {
  const { addCrumb, removeCrumb } = useBreadcrumb();
  const level = useContext(BreadcrumbLevelContext);
  const id = useRef(Symbol());

  useEffect(() => {
    if (!label) return;
    const currentId = id.current;
    addCrumb(currentId, level, { label });
    return () => removeCrumb(currentId);
  }, [label, addCrumb, removeCrumb, level]);
};

export type { BreadcrumbContextValue };
export { BreadcrumbLevelContext, BreadcrumbProvider, useBreadcrumb, useDynamicCrumb };
